import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import HotSidebar from "@/components/feature/HotSidebar";
import useSeo from "@/hooks/useSeo";
import { formatCount } from "@/components/base/PostCard";
import { posts } from "@/mocks/community";
import ArticleActions from "./components/ArticleActions";
import CommentSection from "./components/CommentSection";
import RelatedPosts from "./components/RelatedPosts";

function toIso(time: string) {
  return `${time.replace(" ", "T")}:00+08:00`;
}

export default function PostDetail() {
  const { id } = useParams<{ id: string }>();
  const post = posts.find((item) => item.id === id);

  const jsonLd = useMemo(() => {
    if (!post) return undefined;
    const origin = window.location.origin;
    return [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: post.title,
        description: post.excerpt,
        image: post.cover,
        datePublished: toIso(post.publishedAt),
        dateModified: toIso(post.publishedAt),
        articleSection: post.categoryName,
        keywords: post.tags.join(","),
        author: { "@type": "Person", name: post.author },
        publisher: { "@type": "Organization", name: "爆料社区" },
        mainEntityOfPage: { "@type": "WebPage", "@id": `${origin}/post/${post.id}` },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: origin },
          { "@type": "ListItem", position: 2, name: "话题榜", item: `${origin}/topics` },
          { "@type": "ListItem", position: 3, name: post.title, item: `${origin}/post/${post.id}` },
        ],
      },
    ];
  }, [post]);

  useSeo({
    title: post ? `${post.title} | 爆料社区` : "帖子不存在 | 爆料社区",
    description: post
      ? post.excerpt
      : "该帖子可能已被移除或链接有误，返回话题榜继续浏览今日活跃话题与高互动爆料帖。",
    keywords: post ? post.tags.join(",") : "爆料社区,话题榜",
    path: `/post/${id ?? ""}`,
    type: "article",
    image: post?.cover,
    jsonLd,
  });

  if (!post) {
    return (
      <SiteLayout>
        <div className="mx-auto flex max-w-container flex-col items-center px-4 py-32 text-center md:px-6">
          <i className="ri-file-unknow-line text-5xl text-foreground-400" aria-hidden="true" />
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这篇帖子不在了</h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
            可能是链接有误，或者帖子已根据内容规范被移除。你可以回到话题榜，继续浏览今日活跃话题。
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/topics"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-fire-line" aria-hidden="true" />
              返回话题榜
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              回到首页
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="bg-background-50 pb-16 pt-24 md:pt-28">
        <div className="mx-auto max-w-container px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: post.categoryName, to: `/topics?category=${post.category}` },
              { label: "帖子详情" },
            ]}
          />

          <div className="mt-6 grid gap-10 lg:grid-cols-[1.75fr_1fr]">
            <article>
              <header>
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to={`/topics?category=${post.category}`}
                    className="rounded-md bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-900 transition-colors hover:bg-accent-200"
                  >
                    {post.categoryName}
                  </Link>
                  <span className="inline-flex items-center gap-1 rounded-md bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-700">
                    <i className="ri-fire-line" aria-hidden="true" />
                    热度 {post.heat}
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-foreground-500">
                    <i className="ri-time-line" aria-hidden="true" />
                    {post.publishedAt}
                  </span>
                </div>

                <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
                  {post.title}
                </h1>

                <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-3 border-b border-background-200 pb-5">
                  <span className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 font-heading text-sm font-bold text-background-50">
                      {post.author.slice(0, 1)}
                    </span>
                    <span className="flex flex-col">
                      <span className="text-sm font-medium text-foreground-900">{post.author}</span>
                      <span className="text-xs text-foreground-500">{post.authorRole}</span>
                    </span>
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-foreground-500">
                    <i className="ri-eye-line" aria-hidden="true" />
                    {formatCount(post.views)} 围观
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-foreground-500">
                    <i className="ri-chat-3-line" aria-hidden="true" />
                    {formatCount(post.commentCount)} 条讨论
                  </span>
                </div>
              </header>

              <figure className="mt-6 overflow-hidden rounded-xl bg-background-100">
                <div className="aspect-[16/9] w-full">
                  <img
                    src={post.cover}
                    alt={`${post.title}的现场配图`}
                    title={`${post.title} - 爆料社区`}
                    className="h-full w-full object-top object-cover"
                  />
                </div>
                <figcaption className="px-4 py-3 text-xs text-foreground-500">
                  配图由投稿人提供，仅用于话题说明，具体细节请以当事方发布的信息为准。
                </figcaption>
              </figure>

              <div className="mt-7 space-y-5">
                {post.content.map((paragraph, index) => (
                  <p key={index} className="text-[15px] leading-loose text-foreground-700">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-7 rounded-lg border border-secondary-200 bg-secondary-50 p-4">
                <p className="flex items-start gap-2 text-sm leading-relaxed text-secondary-900">
                  <i className="ri-information-line mt-0.5 shrink-0" aria-hidden="true" />
                  社区提示：本帖内容来自用户投稿，平台已做基础核实但仍可能存在偏差。如你是当事人或掌握新线索，欢迎通过投稿入口补充说明。
                </p>
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/topics?q=${encodeURIComponent(tag)}`}
                    className="rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <ArticleActions baseLikes={Math.round(post.views / 45)} />

              <CommentSection initialComments={post.comments} totalCount={post.commentCount} />

              <RelatedPosts current={post} allPosts={posts} />
            </article>

            <div className="lg:sticky lg:top-24 lg:self-start">
              <HotSidebar />
            </div>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}